import React from 'react';
import useTempImage, { TempImageValue } from './useTempImage';
import ImagePicker, { ImagePickerProps } from '..';
import { useMaybeAvatar } from '../../../../hooks/useAvatar';
import { AvatarChange } from '../../../../primary-record-types/apiTypes/sharedTypes';

export type TempImagePickerOwnProps = {
    value: TempImageValue | null;
    onChange: (value: TempImageValue) => void;
    existingAvatarUrl?: string | null;
    isDisabled?: boolean;
    error?: string;
    testID?: string;
};

export type { TempImageValue };

export type TempImagePickerProps = TempImagePickerOwnProps &
    Pick<
        ImagePickerProps,
        | 'style'
        | 'title'
        | 'isDisabled'
        | 'imagePickerOptions'
        | 'sizeVariant'
        | 'renderImageSourcePicker'
        | 'dimensionValidation'
    >;

const TempImagePicker: React.FC<TempImagePickerProps> = ({ value, onChange, existingAvatarUrl, ...props }) => {
    const existingAvatar = useMaybeAvatar({ avatarAPIUrl: existingAvatarUrl || null });
    // @ts-expect-error TS2322
    const tempAvatar = useTempImage({ value, onChange });
    const getIsLoading = () => {
        const isExistingUploading = existingAvatar && existingAvatar.isLoadingAvatar;

        return tempAvatar.isUploading || isExistingUploading;
    };

    // @ts-expect-error TS18047
    const maybeExistingValue = existingAvatarUrl ? existingAvatar.avatar : value;

    return (
        <ImagePicker
            {...props}
            {...props.imagePickerOptions}
            value={maybeExistingValue}
            onClear={() => {
                // @ts-expect-error TS2345
                onChange(null);
            }}
            // @ts-expect-error TS2322
            isLoadingImage={getIsLoading()}
            onPick={tempAvatar.onFileSelected}
        />
    );
};

export default React.memo(TempImagePicker);

export type PersistedAvatarState = {
    type: 'not-modified';
};

export type PersistedAvatarClearedState = {
    type: 'cleared';
};

export type TempAvatarState = {
    type: 'updated';
    value: TempImageValue;
};

export type TempAndPersistedAvatarState = PersistedAvatarState | PersistedAvatarClearedState | TempAvatarState;

export const avatarStateToAvatarChange = (avatarState: TempAndPersistedAvatarState | null): AvatarChange => {
    if (avatarState?.type === 'updated') {
        return {
            type: 'updated',
            tempAvatarFileId: avatarState.value.tempFileId,
        };
    } else if (avatarState?.type === 'cleared') {
        return {
            type: 'cleared',
        };
    } else if (avatarState?.type === 'not-modified') {
        return {
            type: 'not-modified',
        };
    }

    return { type: 'not-modified' };
};

export type UseTempAndPersistedAvatarStateReturnValue = {
    tempImagePickerProps: TempImagePickerOwnProps;
};

export type UseTempAndPersistedAvatarStateArgs = {
    existingAvatarUrl?: string | null;
    avatarState: TempAndPersistedAvatarState | null;
    /**
     * Avatar initializes from a url.  The component will do an immediate onChange when it maps the input to the state.
     * This flag is used to determine if the initial value is an initialization value or a user change.
     */
    onAvatarStateUpdate: (avatarState: TempAndPersistedAvatarState, isInitializationValue: boolean) => void;
};

/**
 * Utility function for handling the relatively complex state management
 * of having a temp image picker with the possibility of an existing persisted image that may be updated.
 */
export const useTempAndExistingAvatarState = ({
    existingAvatarUrl,
    avatarState,
    onAvatarStateUpdate,
}: UseTempAndPersistedAvatarStateArgs): UseTempAndPersistedAvatarStateReturnValue => {
    const [hasUserInitiatedChange, setHasUserInitiatedChange] = React.useState(false);

    const getValue = () => {
        if (hasUserInitiatedChange) {
            return avatarState?.type === 'updated' ? avatarState.value : null;
        } else {
            return null;
        }
    };

    const tempImagePickerProps: TempImagePickerOwnProps = {
        value: getValue(),
        onChange: (value) => {
            setHasUserInitiatedChange(true);
            if (value) {
                onAvatarStateUpdate(
                    {
                        type: 'updated',
                        value,
                    },
                    false,
                );
            } else {
                onAvatarStateUpdate(
                    {
                        type: 'cleared',
                    },
                    false,
                );
            }
        },
        existingAvatarUrl: avatarState?.type === 'not-modified' || !hasUserInitiatedChange ? existingAvatarUrl : null,
    };

    return {
        tempImagePickerProps,
    };
};

export type TempAndExistingImagePickerOwnProps = {
    existingAvatarUrl?: string | null;
    value: TempAndPersistedAvatarState | null;
    onChange: (value: TempAndPersistedAvatarState | null, isInitializationValue: boolean) => void;
    isDisabled?: boolean;
    error?: string;
    testID?: string;
};

export type TempAndExistingImagePickerProps = TempAndExistingImagePickerOwnProps &
    Pick<ImagePickerProps, 'style' | 'title' | 'isDisabled' | 'imagePickerOptions' | 'sizeVariant'>;

export const TempAndExistingImagePicker: React.FC<TempAndExistingImagePickerProps> = (props) => {
    const { tempImagePickerProps } = useTempAndExistingAvatarState({
        existingAvatarUrl: props.existingAvatarUrl,
        avatarState: props.value,
        onAvatarStateUpdate: props.onChange,
    });

    return <TempImagePicker {...props} {...tempImagePickerProps} />;
};
